<template>
  <div id="guideControlsPannel" class="guide-controls-pannel">
    <div id="guideControlsPannelContainer" class="guide-controls-pannel__container">
      <div class="guide-controls-pannel__info" :class="{'guide-controls-pannel__info_no-logo': !props.channelImage}">
        <div class="guide-controls-pannel__header">
            <div v-show="isHasСhannelNumber" class="guide-controls-pannel__info-channel-number">{{ channelNumber }}</div>
            <img v-if="props.channelImage" :src="props.channelImage" class="guide-controls-pannel__info-channel-logo" :alt="title || ''" loading="lazy" >
        </div>
        <div v-show="title" class="guide-controls-pannel__info-title">{{ title }}</div>
        <div 
          v-show="description" 
          class="guide-controls-pannel__info-subtitle" 
          :class="{'guide-controls-pannel__info-subtitle_isVisible': description}"
        >
        {{ description }}
      </div>
      </div>

      <div class="guide-controls-pannel__controls">
        <button 
          :id="buttonsIds.btnClose"
          ref="refBtnClose"
          class="guide-controls-pannel__controls-single" 
          type="button" 
          :data-keydown="getBottomId(buttonsIds.btnClose)"
          @keydown.up="hideControls"
          @click="closePlayer"
          @focus="onBtnFocus"
        >
          {{ backCaption }}
        </button>

         <button 
          v-if="!isPlayBeginning && isRestartAvailable"
          :id="buttonsIds.btnRestart"
          :ref="buttonsIds.btnRestart"
          class="guide-controls-pannel__controls-single" 
          type="button" 
          :data-keydown="getBottomId(buttonsIds.btnRestart)"
          :data-keyup="getTopId(buttonsIds.btnRestart)"
          @click="onRestart"
          @focus="onBtnFocus"
        >
          {{ reset }} <span class="guide-controls-pannel__controls-single-icon"><common-icon :icon-name="'IconRestart'" /></span>
        </button>

        <button 
          v-if="isRestartAvailable && isPlayBeginning"
          :id="buttonsIds.btnLive"
          :ref="buttonsIds.btnLive"
          class="guide-controls-pannel__controls-single" 
          type="button" 
          :data-keydown="getBottomId(buttonsIds.btnLive)"
          :data-keyup="getTopId(buttonsIds.btnLive)"
          @click="onLive"
          @focus="onBtnFocus"
        >
          {{ live }}
        </button>

        <div class="guide-controls-pannel__controls-group">
          <button 
            :id="buttonsIds.btnPrevChannels"
            :ref="buttonsIds.btnPrevChannels"
            class="guide-controls-pannel__controls-group-circle" 
            type="button" 
            :data-keyup="getTopId(buttonsIds.btnPrevChannels)"
            :data-keydown="getBottomId(buttonsIds.btnPrevChannels)"
            :data-keyright="getRightId(buttonsIds.btnPrevChannels)"
            @focus="onBtnFocus"
            @click="setChannel(false)"
          >
            <player-icon :is-nav-previous="true" :icon-name="'IconNavChannels'" />
          </button>
          <div class="guide-controls-pannel__controls-group-caption">{{ channelsCaption }}</div>
          <button 
            :id="buttonsIds.btnNextChannels"
            ref="btnNextChannels"
            class="guide-controls-pannel__controls-group-circle" 
            type="button" 
            :data-keyup="getTopId(buttonsIds.btnNextChannels)"
            :data-keydown="getBottomId(buttonsIds.btnNextChannels)"
            :data-keyleft="getLeftId(buttonsIds.btnNextChannels)"
            @focus="onBtnFocus"
            @click="setChannel(true)"
          >
            <player-icon :is-nav-previous="false" :icon-name="'IconNavChannels'" />
          </button>
        </div>

        <div class="guide-controls-pannel__controls-group">
          <button 
            :id="buttonsIds.btnVolumeMinus"
            :ref="buttonsIds.btnVolumeMinus"
            class="guide-controls-pannel__controls-group-circle" 
            type="button" 
            :data-keyup="getTopId(buttonsIds.btnVolumeMinus)"
            :data-keydown="getBottomId(buttonsIds.btnVolumeMinus)"
            :data-keyright="getRightId(buttonsIds.btnVolumeMinus)"
            @click="setVolume(false)"
            @focus="onBtnFocus"
          >
            <player-icon :is-nav-plus="false" :icon-name="'IconVolumeChange'" />
          </button>
          <div class="guide-controls-pannel__controls-group-caption">{{ volumeCaption }}</div>
          <button 
            :id="buttonsIds.btnVolumePlus"
            :ref="buttonsIds.btnVolumePlus"
            class="guide-controls-pannel__controls-group-circle" 
            type="button" 
            :data-keyup="getTopId(buttonsIds.btnVolumePlus)"
            :data-keydown="getBottomId(buttonsIds.btnVolumePlus)"
            :data-keyleft="getLeftId(buttonsIds.btnVolumePlus)"
            @click="setVolume(true)"
            @focus="onBtnFocus"
          >
            <player-icon :is-nav-plus="true" :icon-name="'IconVolumeChange'" />
          </button>
        </div>

        <button 
          :id="buttonsIds.btnSubtitles"
          ref="refBtnSubtitles"
          class="guide-controls-pannel__controls-single" 
          :class="{'guide-controls-pannel__controls-single-active': isShowingSubtitles}" 
          type="button" 
          :data-keyup="getTopId(buttonsIds.btnSubtitles)"
          @click="onSubtitles"
          @focus="onBtnFocus"
        >
          {{ subtitlesCaption }} {{ subtitlesCaptionState }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
    import { type PropType, type Ref, ref, computed } from "vue";
    import { Strings } from "@/locales/types/enums";
    import type { Channel, PlayItem } from "@ca.vmedia.tv/domains";
    import { useI18n } from "vue-i18n";
    import type { ExposeInterface } from "./types";
    import { usePlayerStore } from "@/stores/Player";
    import { useGetDescription } from "@/composables/useGetDescription";

    interface IButtonsIds {
        btnPrevChannels: string;
        btnNextChannels: string;
        btnVolumeMinus: string;
        btnVolumePlus: string;
        btnSubtitles: string;
        btnClose: string;
        btnRestart: string;
        btnLive: string;
    }

    const PlayerStoreModule = usePlayerStore();
    const {
        setPlayFromBeginning,
        setVolume: setVolumetoStore
    } = PlayerStoreModule;

    const props = defineProps({
        channelNumber: {
            type: Number,
            required: false,
            default: 0
        },
        channelImage: {
            type: [String, null] as PropType<string | null>,
            required: false,
            default: ''
        },
        playItem: {
            type: [Object, null] as PropType<PlayItem | Channel | null>,
            required: true
        },
        volume: {
            type: Number,
            required: true
        },
        isModalClosed: {
            type: Boolean,
            required: true
        },
        isShowingSubtitles: {
            type: Boolean,
            required: true
        },
        isPlayBeginning: {
            type: Boolean,
            required: true
        },
        isPlayingChannel: {
            type: Boolean,
            required: true
        }
    });

    const emit = defineEmits([
        'closePlayer',
        'set-next-channel',
        'set-prev-channel',
        'show-subtitles',
        'restart',
        'go-live',
        'hide',
        'refreshActionTime'

    ]);

    const { t } = useI18n();
    const subtitlesCaption: string = t(Strings.PLAYER_SUBTITLES_ONLY).toString();
    const backCaption: string = t(Strings.PLAYER_BACK).toString();
    const channelsCaption: string = t(Strings.PLAYER_CHANNELS).toString();
    const live: string = t(Strings.HOME_LIVE).toString();
    const reset: string = t(Strings.PLAYER_RESET).toString();
    const buttonsIds: IButtonsIds = {
        btnPrevChannels: "btnPrevChannels",
        btnNextChannels: "btnNextChannels",
        btnVolumeMinus: "btnVolumeMinus",
        btnVolumePlus: "btnVolumePlus",
        btnSubtitles: "btnSubtitles",
        btnClose: "btnClose",
        btnRestart: "btnRestart",
        btnLive: "btnLive",
    };
    const refBtnClose: Ref<HTMLButtonElement | null> = ref(null);
    const refBtnSubtitles: Ref<HTMLButtonElement | null> = ref(null);


    const isHasСhannelNumber = computed((): boolean => props.channelNumber !== 0);
    const subtitlesCaptionState = computed((): string => {
        const state = props.isShowingSubtitles ? Strings.PLAYER_SUBTITLE_ON : Strings.PLAYER_SUBTITLE_OFF;
        return t(state).toString();
    });
    const title = computed((): string | null => props.playItem?.name ?? null);
    const { description } = useGetDescription(props.playItem);
    const isRestartAvailable = computed((): boolean => (props.playItem?.isRestartAvailable ?? false) && !props.isPlayingChannel);
    const volumeCaption = computed((): string => t(Strings.PLAYER_VOLUME).toString() + " " + props.volume + "%");
    


    const onRestart = () => {
        setPlayFromBeginning(true);
        emit("restart");
    };
    const onLive = () => {
        setPlayFromBeginning(false);
        emit("go-live");
    };
    const onBtnFocus = () => {
        emit("refreshActionTime");
    };
    const hideControls = () => {
        setTimeout(() => {
            emit("hide");
        }, 325);
    };
    const getLeftId = (currentId: string): string | null => {
        if (currentId === buttonsIds.btnVolumePlus) {
            return buttonsIds.btnVolumeMinus;
        }
        if (currentId === buttonsIds.btnNextChannels) {
            return buttonsIds.btnPrevChannels;
        }
        return null;
    };
    const getRightId = (currentId: string): string | null => {
        if (currentId === buttonsIds.btnVolumeMinus) {
            return buttonsIds.btnVolumePlus;
        }
        if (currentId === buttonsIds.btnPrevChannels) {
            return buttonsIds.btnNextChannels;
        }
        return null;
    };
    const getTopId = (currentId: string): string | null => {
        if (currentId === buttonsIds.btnLive || currentId === buttonsIds.btnRestart) {
            return buttonsIds.btnClose;
        }
        if (currentId === buttonsIds.btnVolumeMinus) {
            return buttonsIds.btnPrevChannels;
        }
        if (currentId === buttonsIds.btnVolumePlus) {
            return buttonsIds.btnNextChannels;
        }
        if (currentId === buttonsIds.btnSubtitles) {
            return buttonsIds.btnVolumeMinus;
        }

        if (currentId === buttonsIds.btnPrevChannels || currentId === buttonsIds.btnNextChannels) {
            if (isRestartAvailable.value) {
                if (!props.isPlayBeginning) {
                    return buttonsIds.btnRestart;
                } 
                return buttonsIds.btnLive;
            }
            return buttonsIds.btnClose;
        }
        return null;
    };
    const getBottomId = (currentId: string): string | null => {
        if (currentId === buttonsIds.btnLive || currentId === buttonsIds.btnRestart) {
            return buttonsIds.btnPrevChannels;
        }
        if (currentId === buttonsIds.btnClose) {
            if (isRestartAvailable.value) {
                if (!props.isPlayBeginning) {
                    return buttonsIds.btnRestart;
                } 
                return buttonsIds.btnLive;
            }
            return buttonsIds.btnPrevChannels;
        }
        if (currentId === buttonsIds.btnPrevChannels) {
            return buttonsIds.btnVolumeMinus;
        }
        if (currentId === buttonsIds.btnNextChannels) {
            return buttonsIds.btnVolumePlus;
        }
        if (currentId === buttonsIds.btnVolumeMinus || currentId === buttonsIds.btnVolumePlus) {
            return buttonsIds.btnSubtitles;
        }
        return null;
    };
    const focusDefault = () => {
        if (props.isModalClosed) {
            if (refBtnSubtitles.value) {
                refBtnSubtitles.value.focus();
            }
        } else {
            if (refBtnClose.value) {
                refBtnClose.value.focus();
            }
        }
    };
    const onSubtitles = () => {
        emit("show-subtitles");
    };
    const setChannel = (goToNextChannel: boolean): void => {
        if (goToNextChannel) {
            emit("set-next-channel");
        } else {
            emit("set-prev-channel");
        }
    };
    const setVolume = (increase: boolean): void => {
        const interval: number = 10;
        let volume: number = props.volume;

        if (increase) {
            if (volume != 100) {
            volume+=interval;
            }
        } else {
            if (volume != 0) {
            volume-=interval;
            }
        }

        setVolumetoStore(volume);
    };
    const closePlayer = () => {
        emit("closePlayer");
    }

    defineExpose<ExposeInterface>({
        focusDefault
    });
</script>


<style lang="scss">
    @import url("styles.scss");
</style>